// LoanCapitalProducts.ts

import {
  type CombinedLoanAndProductsQuery,
  type ProductType,
} from "@/generated/graphql"
import { abbreviateCurrency } from "@/utils/NumberUtils"

export const STANDARD_CAPITAL_PRODUCT_NAME: string = "The Standard"

export const STANDARD_CAPITAL_PRODUCT_DESCRIPTION: string =
  "Our core capital product, moderate leverage to boost total profit"

export type CapitalStandardSubProduct =
  | "standard_low_interest"
  | "standard_low_cash"

export type CapitalProductSlug =
  | "standard"
  | "standard_combined"
  | "refinance_rehab"
  | "double_double"
  | "zero_gravity"
  | "altitude"

export type CapitalProductTitle =
  | "Standard"
  | "Refinance Rehab"
  | "Double Double"
  | "Zero Gravity"
  | "Altitude"

export type CapitalProductQualifierType = ">=" | ">" | "=" | "<=" | "<" | ""

export interface CapitalProductOptionProps {
  name: string
  slug: string
  description: string
  image: string
}

export const CAPITAL_PRODUCTS: CapitalProductOptionProps[] = [
  {
    name: "Standard",
    slug: "standard",
    description:
      "Our core capital product, moderate\nleverage to boost total profit",
    image: "gold-standard-trophy",
  },
  {
    name: STANDARD_CAPITAL_PRODUCT_NAME,
    slug: "standard_low_interest",
    description: STANDARD_CAPITAL_PRODUCT_DESCRIPTION,
    image: "gold-standard-trophy",
  },
  {
    name: "Altitude",
    slug: "altitude",
    description: "For elevated leverage with competitive pricing",
    image: "gold-standard-trophy",
  },
  {
    name: STANDARD_CAPITAL_PRODUCT_NAME,
    slug: "standard_low_cash",
    description: STANDARD_CAPITAL_PRODUCT_DESCRIPTION,
    image: "gold-standard-trophy",
  },
  {
    name: "Refi+Rehab",
    slug: "refinance_rehab",
    description:
      "Refinancing option for rehab projects, aimed at maximizing ROI",
    image: "double-double-bills",
  },
  {
    name: "Double Double",
    slug: "double_double",
    description:
      "Maximize your leverage to increase\nROI on high-quality deals",
    image: "double-double-bills",
  },
  {
    name: "Zero Gravity",
    slug: "zero_gravity",
    description:
      "No out of pocket lender fees,\nno interest payments for 6 months",
    image: "",
  },
]

export const DEPRECATED_CAPITAL_PRODUCTS: string[] = [
  "double_double",
  "DD",
] as const

export interface CapitalProductRequirement {
  label: string
  userValue: number | string | null
  qualifiedValue: number | string | null
  unit: string
  qualifier?: CapitalProductQualifierType
  isPrefix?: boolean
}

export const getCapitalProductBySlug = (
  slug: CapitalProductSlug
): CapitalProductOptionProps | undefined => {
  return CAPITAL_PRODUCTS.find((product) => product.slug === slug)
}

export const getCapitalProductByTitle = (
  title: CapitalProductTitle
): CapitalProductOptionProps | undefined => {
  return CAPITAL_PRODUCTS.find((product) => product.name === title)
}

export const getCapitalProductNameBySlug = (
  slug: CapitalProductSlug
): string | undefined => {
  if (slug === "standard_combined") {
    return "Standard"
  }
  const product = getCapitalProductBySlug(slug)
  return product ? product.name : undefined
}

export const getCapitalProductDescriptionBySlug = (
  slug: CapitalProductSlug
): string | undefined => {
  const product = getCapitalProductBySlug(slug)
  return product ? product.description : undefined
}

export const getNonQualifiedRequirements = (
  cardData: ProductType,
  storedLoan: NonNullable<CombinedLoanAndProductsQuery["loanApplication"]>
): CapitalProductRequirement[] => {
  const getValueOrFallbackValue = (primary: any, fallback: any): number => {
    if (typeof primary === "number" && primary !== 0) {
      return primary
    }
    if (typeof fallback === "number") {
      return fallback
    }
    return 0
  }

  const requirements: CapitalProductRequirement[] = [
    {
      label: "Flips/BRRRR",
      userValue: getValueOrFallbackValue(
        cardData.flips,
        storedLoan.flipsCompleted || 0
      ),
      qualifiedValue: cardData.qualifiedExperience ?? 0,
      unit: "",
      qualifier: ">=",
      isPrefix: false,
    },
    {
      label: "Purchase Price",
      userValue: getValueOrFallbackValue(
        cardData.purchasePrice,
        storedLoan?.purchasePrice || 0
      ),
      qualifiedValue: cardData.qualifiedPurchasePrice ?? 0,
      unit: "$",
      qualifier: ">=",
      isPrefix: true,
    },
    {
      label: "Credit Score",
      userValue: getValueOrFallbackValue(
        cardData.creditScore,
        storedLoan.creditScore as unknown as number
      ),
      qualifiedValue: cardData.qualifiedCreditScore ?? 0,
      unit: "",
      qualifier: ">=",
      isPrefix: false,
    },
    {
      label: "Profit/ROI",
      userValue: getValueOrFallbackValue(
        Math.round(cardData.returnOnInvestment || 0),
        Math.round((storedLoan.grossProfit || 0) / storedLoan.purchasePrice)
      ),
      qualifiedValue: cardData.qualifiedReturnOnInvestment ?? 0,
      unit: "%",
      qualifier: ">=",
      isPrefix: false,
    },
    {
      label: "Loan Amount",
      userValue: getValueOrFallbackValue(
        cardData.totalLoanAmount,
        storedLoan.totalLoanAmount
      ),
      qualifiedValue: cardData.qualifiedLoanAmount ?? 0,
      unit: "$",
      qualifier: ">=",
      isPrefix: true,
    },
    {
      label: "Loan Amount ",
      userValue: getValueOrFallbackValue(
        cardData.totalLoanAmount,
        storedLoan.totalLoanAmount
      ),
      qualifiedValue: cardData.qualifiedMaxLoanAmount ?? 0,
      unit: "$",
      qualifier: "<=",
      isPrefix: false,
    },
    {
      label: "Hold Period",
      userValue: getValueOrFallbackValue(
        cardData.holdPeriod,
        storedLoan.holdPeriod
      ),
      qualifiedValue: cardData.qualifiedHoldPeriod ?? 0,
      unit: " mos",
      qualifier: "<=",
      isPrefix: false,
    },
    {
      label: "Property Location**",
      userValue: cardData.state || "-",
      qualifiedValue: "in licensed state",
      unit: "",
      qualifier: "",
      isPrefix: false,
    },
    // other requirements as needed...
  ]

  // These requirements are kept in the list bec. they are not met and should display
  return requirements.filter((req) => {
    if (req.label === "Property Location**") {
      if (!cardData.nonLicensedStates || !cardData.state) {
        return false
      }
      return cardData.nonLicensedStates.includes(cardData.state?.toLowerCase())
    }

    switch (req.qualifier) {
      case ">=":
        // Include if user value is less than the qualified value
        return (req.userValue || 0) < (req.qualifiedValue || 0)
      case ">":
        // Include if user value is less than or equal to the qualified value
        return (req.userValue || 0) <= (req.qualifiedValue || 0)
      case "=":
        // Include if user value is not equal to the qualified value
        return (req.userValue || 0) !== (req.qualifiedValue || 0)
      case "<=":
        // Include if user value is greater than the qualified value
        return (req.userValue || 0) > (req.qualifiedValue || 0)
      case "<":
        // Include if user value is greater than or equal to the qualified value
        return (req.userValue || 0) >= (req.qualifiedValue || 0)
      default:
        // If no qualifier is specified, assume it's a custom condition or true by default
        return true
    }
  })
}

export const formatRequirementValue = (
  value: number | string | null,
  unit: string,
  qualifier: CapitalProductQualifierType,
  isPrefix: boolean
): string => {
  if (unit === "$" && typeof value === "number") {
    value = abbreviateCurrency(value)
    unit = ""
  }
  const formattedValue = isPrefix ? `${unit}${value}` : `${value}${unit}`
  return `${qualifier} ${formattedValue}`
}

export const formatUserActualValue = (
  userValue: number | string | null,
  unit: string,
  isPrefix: boolean
): string => {
  let value = userValue
  if (unit === "$" && typeof value === "number") {
    value = abbreviateCurrency(value)
    unit = ""
  }
  const formattedValue = isPrefix ? `${unit}${value}` : `${value}${unit}`
  return formattedValue
}

export const formatNonLicensedStates = (
  nonLicensedStates: string[]
): string => {
  if (!nonLicensedStates || nonLicensedStates.length === 0) return ""
  return nonLicensedStates.join(", ").toUpperCase()
}

export const allProductsAreIneligible = (loanData?: ProductType[]): boolean => {
  if (loanData?.length === 0) {
    return true
  }
  return loanData?.every((product) => product.isEligible === false) ?? true
}

// Extended type to include products array for combined product
export interface CombinedProductType extends ProductType {
  products: ProductType[]
}

// Utility function to merge standard_low_cash and standard_low_interest into a combined product
export const mergeLoanProducts = (
  loanProducts: ProductType[]
): Array<ProductType | CombinedProductType> => {
  return loanProducts.reduce(
    (result: Array<ProductType | CombinedProductType>, product) => {
      // Exclude any products with just "standard" (without underscore)
      if (product.product === "standard") {
        return result
      }

      // Handle standard_low_cash and standard_low_interest
      if (
        product.product === "standard_low_interest" ||
        product.product === "standard_low_cash"
      ) {
        const existing = result.find(
          (p) =>
            p.product === "standard_low_interest" ||
            p.product === "standard_low_cash"
        )

        if (existing && !("products" in existing)) {
          // Group the two products into a new product called "standard_combined"
          return [
            ...result.filter(
              (p) =>
                p.product !== "standard_low_interest" &&
                p.product !== "standard_low_cash"
            ),
            {
              product: "standard_combined",
              products: [product, existing], // Keep both products in array for reference

              // Merge the interestRate and cashToClose values explicitly
              interestRate: product.interestRate || existing.interestRate,
              cashToClose: product.cashToClose || existing.cashToClose,
            } as CombinedProductType,
          ]
        }
      }

      // Return the product if not part of standard_low_interest/standard_low_cash
      return [...result, product]
    },
    []
  )
}

// Filter out any product where the product name starts with "standard_"
export const loanProductsExcludingCombinedStandard = (
  loanProducts: ProductType[]
): ProductType[] => {
  return loanProducts.filter((product) => {
    if (!product?.product) {
      return false
    }
    return !product.product.startsWith("standard_")
  })
}

export type PostCloseHubSpotProducts = "Standard" | "DD" | "0G" | "Altitude"

export type AllProductTypes =
  | CapitalProductSlug
  | CapitalStandardSubProduct
  | PostCloseHubSpotProducts

export const isZeroGravity = (product: AllProductTypes): boolean => {
  return product === "zero_gravity" || product === "0G"
}

export const isDoubleDouble = (product: AllProductTypes): boolean => {
  return product === "double_double" || product === "DD"
}

export const isStandard = (product: AllProductTypes): boolean => {
  return (
    product === "standard" ||
    product === "standard_combined" ||
    product === "standard_low_interest" ||
    product === "standard_low_cash" ||
    product === "Standard"
  )
}

export const activeProducts = (products: ProductType[]) => {
  return products.filter(
    (item) =>
      !DEPRECATED_CAPITAL_PRODUCTS.includes(item.product as CapitalProductSlug)
  )
}
